<script setup lang="ts">
import { ChevronsLeft, ChevronLeft, ChevronsRight, ChevronRight } from 'lucide-vue-next'
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev
} from '~/components/ui/pagination'
import type { IResponsePagination } from '~/types'

const route = useRoute()

const getPageNumber = (pathName: string) => {
  const match = pathName.match(/[?&]page=(\d+)/)
  return match ? Number(match[1]) : 1
}

// Active filters live in the query too, so paging must carry them along instead
// of rebuilding the link from the page number alone.
const getPathName = (page: number) => {
  const { page: _currentPage, ...restQuery } = route.query

  return {
    path: route.path.split('?')[0],
    query: page <= 1 ? restQuery : { ...restQuery, page }
  }
}

const props = defineProps<Omit<IResponsePagination, 'data'>>()

const prev = computed(() => (props.links.prev ? Number(getPageNumber(props.links.prev)) : 0))
const next = computed(() => (props.links.next ? Number(getPageNumber(props.links.next)) : 0))

const emit = defineEmits({
  change: (page: number) => page
})

watch(
  () => route.query.page,
  (page) => {
    emit('change', Number(page || 1))
    if (import.meta.client) window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }
)
</script>

<template>
  <Pagination
    v-if="props.meta.last_page > 1"
    v-slot="{ page }"
    :items-per-page="props.meta.per_page"
    :total="props.meta.total"
    :sibling-count="$device.isMobile ? 0 : 1"
    :page="props.meta.current_page"
    show-edges
    class="flex justify-center"
    aria-label="pagination"
    data-testid="pagination"
  >
    <PaginationList v-slot="{ items }" class="flex items-center gap-1">
      <PaginationFirst v-if="!$device.isMobile">
        <NuxtLink
          :to="getPathName(1)"
          :aria-disabled="props.meta.current_page === 1"
          :class="{ 'pointer-events-none opacity-50': props.meta.current_page === 1 }"
        >
          <ChevronsLeft class="size-4" />
        </NuxtLink>
      </PaginationFirst>

      <PaginationPrev>
        <NuxtLink
          :to="getPathName(prev)"
          :aria-disabled="props.meta.current_page === 1"
          :class="{ 'pointer-events-none opacity-50': props.meta.current_page === 1 }"
        >
          <ChevronLeft class="size-4" />
        </NuxtLink>
      </PaginationPrev>
      <template v-for="(item, index) in items">
        <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
          <Button
            as-child
            class="h-9 w-9 p-0"
            :variant="item.value === page ? 'blue' : 'outline-gray'"
          >
            <NuxtLink :to="getPathName(item.value)">
              {{ item.value }}
            </NuxtLink>
          </Button>
        </PaginationListItem>
        <PaginationEllipsis v-else :key="item.type" :index="index" />
      </template>
      <PaginationNext>
        <NuxtLink
          :to="getPathName(next || props.meta.last_page)"
          :aria-disabled="props.meta.current_page === props.meta.last_page"
          :class="{
            'pointer-events-none opacity-50': props.meta.current_page === props.meta.last_page
          }"
        >
          <ChevronRight class="size-4" />
        </NuxtLink>
      </PaginationNext>

      <PaginationLast v-if="!$device.isMobile">
        <NuxtLink
          :to="getPathName(props.meta.last_page)"
          :aria-disabled="props.meta.current_page === props.meta.last_page"
          :class="{
            'pointer-events-none opacity-50': props.meta.current_page === props.meta.last_page
          }"
        >
          <ChevronsRight class="size-4" />
        </NuxtLink>
      </PaginationLast>
    </PaginationList>
  </Pagination>
</template>
